import gaEvents from '~js/ga-events'
import {
  isIframe,
  isIOS
} from '~js/utils'
import { eventEmitter } from '@/utils/event'
import {
  KRPANO_SETTING,
  CATEGORYNAMES
} from '~js/constants'
import queryStr from '~js/query-string'

const state = {
  krpanoEl: null,
  krpanoHooks: eventEmitter(),
  isKrpanoActive: false,
  krpanoCamera: {
    krpanoLookAtH: 0,
    krpanoLookAtV: 0,
    krpanoFov: 120,
    isCameraRotating: false,
    autoStartRotateTimer: null
  },
  krpanoXOffset: 90, // because A-frame <a-sky> has rotation 90
  isGyroEnabled: false,
  isGyroFromIframe: false
}

export const getters = {
  krpanoEl: state => state.krpanoEl,
  krpanoHooks: state => state.krpanoHooks,
  isKrpanoActive: state => state.isKrpanoActive,
  krpanoLookAtH: state => state.krpanoCamera.krpanoLookAtH,
  krpanoLookAtV: state => state.krpanoCamera.krpanoLookAtV,
  krpanoFov: state => state.krpanoCamera.krpanoFov,
  isCameraRotating: state => state.krpanoCamera.isCameraRotating,
  autoStartRotateTimer: state => state.krpanoCamera.autoStartRotateTimer,
  krpanoXOffset: state => state.krpanoXOffset,
  isGyroEnabled: state => state.isGyroEnabled,
  isGyroFromIframe: state => state.isGyroFromIframe,
  useAutopilot: (state, getters) => {
    const canAutoPilot = getters.customSetting.useAutopilot
    return canAutoPilot && queryStr.toAutoPilot
  }
}

export const actions = {
  setKrpanoEl ({ commit }, krpanoEl = null) {
    commit('SET_KRPANO_EL', krpanoEl)
  },

  setKrpanoActive ({ commit }, bool = false) {
    commit('SET_KRPANO_ACTIVE', bool)
  },

  setKrpanoCamera ({ commit }, camera = { h: 0, v: 0, fov: 120 }) {
    commit('SET_KRPANO_CAMERA', camera)
  },

  startAutoRotate ({ commit, state, dispatch, rootState, getters }, customConfig = {}) {
    if (process.env.AUTO_ROTATE == null) return
    if (queryStr.notToAutoRotate) return
    if (state.krpanoEl == null) return
    if (state.isGyroEnabled === true) return
    if (getters.isLiveStreamActive) return
    if (getters.isPilotRecording || getters.isPilotPlaying) return
    const autoPilotAngle = Number(rootState.route.query.autopilotAngle) || 360
    const panAngle = (getters.useAutopilot ? autoPilotAngle : 360) * (customConfig.revert ? 1 : -1)
    const speed = getters.useAutopilot ? KRPANO_SETTING.speed.fast : KRPANO_SETTING.speed.slow
    window.krpanoObj.call(`pan(${panAngle}, ${speed}, "pan");`)
    state.krpanoHooks.set('pan', () => {
      const isMarkerPoint = false
      const panoramas = rootState.panoramas.panoramas
      const currentPanorama = rootState.panoramas.currentPanorama
      const currentPanoamaIndex = panoramas.indexOf(currentPanorama)
      const nextPanorama = panoramas.filter((pano, idx) => { // 按升序找到下個非廁所的pano 找不到就首張pano
        if ([CATEGORYNAMES.toilet, CATEGORYNAMES.bathroom].includes(pano.category)) return false
        if (idx <= currentPanoamaIndex) return false
        return true
      }).shift() || panoramas[0]
      if (!getters.useAutopilot) dispatch('startAutoRotate', customConfig)
      else if (nextPanorama === currentPanorama) dispatch('startAutoRotate', customConfig)
      else window.krpanoObj.call(`prepare_change_scene('panorama_${nextPanorama.objectId}',${nextPanorama.objectId}, 'autopilot', ${state.krpanoCamera.krpanoLookAtH + state.krpanoXOffset}, ${isMarkerPoint});`)
    })
    commit('SET_CAMERA_ROTATING', true)
  },

  stopAutoRotate ({ dispatch, commit, state, rootState }, customConfig = {}) {
    const config = Object.assign({
      shouldAutoStartRotate: false,
      duration: KRPANO_SETTING.time.autoStartAutoRotate * 1000,
      stopMethod: ''
    }, customConfig)
    if (state.krpanoCamera.autoStartRotateTimer != null) {
      window.clearTimeout(state.krpanoCamera.autoStartRotateTimer)
      commit('SET_AUTO_START_ROTATE_TIMER', null)
    }
    // if (config.shouldAutoStartRotate) {
    //   const autoStartRotateTimer = window.setTimeout(() => {
    //     dispatch('startAutoRotate')
    //     commit('SET_AUTO_START_ROTATE_TIMER', null)
    //   }, config.duration)
    //   commit('SET_AUTO_START_ROTATE_TIMER', autoStartRotateTimer)
    // }
    if (process.env.AUTO_ROTATE == null) return
    if (state.krpanoCamera.isCameraRotating === false) return
    if (config.stopMethod) {
      gaEvents.sendEvent('Panorama', `StopAutoRotateBy${config.stopMethod}`, rootState.panoramas.currentPanorama.objectId)
    }
    window.krpanoObj.call('stoptween(view.hlookat);')
    commit('SET_CAMERA_ROTATING', false)
  },

  startGyro ({ dispatch, commit, state }) {
    if (state.isGyroEnabled === false && window.krpanoObj) {
      if (state.isGyroFromIframe) {
        const gyroMessage = window.krpanoObj.get('plugin[gyroMessage]')
        if (gyroMessage) {
          gyroMessage.enable()
        }
      } else {
        window.krpanoObj.call('start_gyro();')
      }
      commit('SET_GYRO_ENABLED', true)
      dispatch('stopAutoRotate')
    } else {
      console.log('gyro doesn\'t start')
    }
  },

  toggleGyro ({ dispatch, state, rootState }) {
    if (state.isGyroEnabled === true) {
      dispatch('stopGyro')
    } else {
      dispatch('startGyro')
    }
  },

  stopGyro ({ dispatch, commit, state, rootState, getters }) {
    if (state.isGyroEnabled === true && window.krpanoObj) {
      if (state.isGyroFromIframe) {
        const gyroMessage = window.krpanoObj.get('plugin[gyroMessage]')
        if (gyroMessage) {
          gyroMessage.disable()
        }
      } else {
        window.krpanoObj.call('stop_gyro();')
      }
      gaEvents.sendEvent('Building', 'TurnGyroOff', rootState.route.params.buildingId || rootState.buildings.currentBuilding.objectId || '')
      commit('SET_GYRO_ENABLED', false)
      dispatch('stopAutoRotate', {
        shouldAutoStartRotate: !getters.isVrMode && !state.isGyroEnabled
      })
    } else {
      console.log('gyro doesn\'t stop')
    }
  },

  initGyroFromIframe ({ commit, dispatch }, bool = false) {
    function receiveMessage (event) {
      // Do we trust the sender of this message?  (might be
      // different from what we originally opened, for example).
      // if (event.origin !== "http://example.com")
      //   return;
      const data = JSON.parse(event.data)
      if (data.alpha !== null && data.beta !== null && data.gamma !== null) {
        dispatch('initGyroFromIframe', true)
      }
      window.removeEventListener('message', receiveMessage, false)
    }

    if (isIframe() && isIOS()) {
      window.addEventListener('message', receiveMessage, false)
      commit('SET_GYRO_FROM_IFRAME', bool)
    }
  },

  zoomIn ({ state }) {
    if (window.krpanoObj) {
      window.krpanoObj.call('zoom_in();')
    }
  },

  zoomOut ({ state }) {
    if (window.krpanoObj) {
      window.krpanoObj.call('zoom_out();')
    }
  }
}

export const mutations = {
  SET_KRPANO_EL (state, krpanoEl = null) {
    state.krpanoEl = krpanoEl
  },

  SET_KRPANO_ACTIVE (state, bool = false) {
    state.isKrpanoActive = bool
  },

  SET_KRPANO_CAMERA (state, camera = {}) {
    state.krpanoCamera.krpanoLookAtH = camera.h
    state.krpanoCamera.krpanoLookAtV = camera.v
    state.krpanoCamera.krpanoFov = camera.fov
  },

  SET_CAMERA_ROTATING (state, bool = false) {
    if (process.env.AUTO_ROTATE) {
      if (state.krpanoCamera) {
        state.krpanoCamera.isCameraRotating = bool
      }
    }
  },

  SET_AUTO_START_ROTATE_TIMER (state, timer = null) {
    if (process.env.AUTO_ROTATE) {
      if (state.krpanoCamera) {
        state.krpanoCamera.autoStartRotateTimer = timer
      }
    }
  },

  SET_GYRO_ENABLED (state, bool = false) {
    state.isGyroEnabled = bool
  },

  SET_GYRO_FROM_IFRAME (state, bool = false) {
    state.isGyroFromIframe = bool
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
